import React, { useMemo } from "react";
import { Layout, Menu, Button } from "antd";
import {
  AppstoreOutlined,
  BankOutlined,
  FilePdfOutlined,
  NodeIndexOutlined,
  RobotOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import Modal from "../ui/Modal";

const { Sider } = Layout;
const iconMap = {
  dashboard: <AppstoreOutlined />,
  businessDetails: <BankOutlined />,
  intents: <NodeIndexOutlined />,
  pdfDocuments: <FilePdfOutlined />,
  agentToneControl: <RobotOutlined />,
  settings: <SettingOutlined />,
};

function toMenuItem(item) {
  const icon = iconMap[item.key] || iconMap[item.icon] || null;
  return {
    key: item.key,
    icon,
    label: item.label,
  };
}

export function Sidebar({
  appMeta,
  navigation = [],
  activeKey,
  onLogout,
  setCurrentPage,
  collapsed = false,
  mobile = false,
  open = false,
  onRequestClose,
  onToggleSidebar,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [logoutModalOpen, setLogoutModalOpen] = React.useState(false);
  const routeMap = {
    dashboard: "/dashboard",
    businessDetails: "/business/details",
    intents: "/business/intents",
    pdfDocuments: "/business/pdfs",
    agentToneControl: "/agent-tone-control",
    settings: "/settings",
  };
  const menuItems = useMemo(
    () =>
      navigation.flatMap((item) => {
        if (item.children?.length) {
          return item.children.map((child) => toMenuItem(child));
        }
        return [toMenuItem(item)];
      }),
    [navigation],
  );

  const handleMenuClick = ({ key }) => {
    const path = routeMap[key];
    if (path) {
      navigate(path);
      onRequestClose?.();
    }
  };

  const effectiveCollapsed = mobile ? !open : collapsed;
  const handleConfirmLogout = () => {
    onLogout?.();
    navigate("/login");
    onRequestClose?.();
    setLogoutModalOpen(false);
  };

  return (
    <>
      <Sider
        className={`app-sider ${effectiveCollapsed ? "is-collapsed" : ""} ${mobile ? "is-mobile" : ""} ${open ? "is-open" : ""}`}
        width={262}
        collapsedWidth={mobile ? 0 : 88}
        collapsible
        trigger={null}
        collapsed={effectiveCollapsed}
        theme="light"
      >
        <div className="sider-inner">
          <div className={`sidebar-brand ${effectiveCollapsed ? "is-collapsed" : ""}`}>
            <Button
              type="text"
              className="sidebar-collapse-toggle"
              aria-label={mobile ? "Close sidebar" : "Toggle sidebar"}
              icon={
                mobile ? (
                  <CloseOutlined />
                ) : effectiveCollapsed ? (
                  <MenuUnfoldOutlined />
                ) : (
                  <MenuFoldOutlined />
                )
              }
              onClick={() => {
                if (mobile) {
                  onRequestClose?.();
                  return;
                }
                onToggleSidebar?.();
              }}
            />
            <img
              src="/fw_logo.png"
              alt={`${appMeta.productName} logo`}
              className="sidebar-brand-logo sidebar-brand-logo-lg"
            />
          </div>

          <Menu
            className="sidebar-menu"
            mode="inline"
            selectedKeys={[
              Object.keys(routeMap).find((key) =>
                location.pathname.startsWith(routeMap[key]),
              ),
            ]}
            items={menuItems}
            onClick={handleMenuClick}
          />

          <div className="sidebar-footer">
            <Button
              type="default"
              icon={<LogoutOutlined />}
              block
              onClick={() => setLogoutModalOpen(true)}
            >
              {!effectiveCollapsed ? "Logout" : null}
            </Button>
          </div>
        </div>
      </Sider>

      <Modal
        open={logoutModalOpen}
        title="Confirm Logout"
        onClose={() => setLogoutModalOpen(false)}
        actions={[
          <Button
            key="cancel-logout"
            type="default"
            onClick={() => setLogoutModalOpen(false)}
          >
            Cancel
          </Button>,
          <Button
            key="confirm-logout"
            type="primary"
            onClick={handleConfirmLogout}
          >
            Logout
          </Button>,
        ]}
      >
        <p>Are you sure you want to logout?</p>
      </Modal>
    </>
  );
}

export default Sidebar;
